import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Search, ExternalLink, KeyRound, ChevronDown } from "lucide-react";
import { api, errMsg, CATEGORY_META, TYPE_LABEL, isPaidProvider } from "@/lib/api";
import { CategoryBadge, PageHeader } from "@/components/Common";

const Row = ({ k, v }) => (
  <div className="grid grid-cols-[120px_1fr] gap-3 text-xs"><span className="text-slate-500">{k}</span><span className="text-slate-300">{v || "unknown"}</span></div>
);

const ProviderCard = ({ p }) => {
  const [open, setOpen] = useState(false);
  return (
    <article className={`glass flex flex-col p-6 fade-up ${isPaidProvider(p) ? "border-red-500/30" : ""}`} data-testid={`catalog-card-${p.id}`}>
      <div className="flex items-start justify-between gap-3">
        <h3 className="text-lg font-semibold text-white">{p.name}</h3>
        <CategoryBadge category={p.cost_status} testid={`catalog-category-${p.id}`} />
      </div>
      <div className="mt-3 flex flex-wrap gap-1.5">
        {p.capabilities.map((c) => <span key={c} className="rounded border border-white/10 px-1.5 py-0.5 text-[11px] text-slate-400">{c}</span>)}
        <span className="rounded border border-white/10 px-1.5 py-0.5 text-[11px] text-slate-400">{p.key_required ? "API-Key nötig" : "kein Key nötig"}</span>
        {!p.enabled && <span className="rounded border border-red-500/30 px-1.5 py-0.5 text-[11px] text-red-300">vom Admin deaktiviert</span>}
      </div>
      <p className="mt-4 text-sm text-slate-300">{p.limits}</p>
      <div className="mt-3 text-[11px] text-slate-500" data-testid={`catalog-verified-${p.id}`}>
        Geprüft: {p.last_verified_at} · {{ verified: "aus offizieller Quelle", verified_live: "live verifiziert", official_docs: "offizielle Doku, nicht live getestet", community_list: "nur Community-Liste – vor Nutzung prüfen" }[p.verification_status] || "nicht verifiziert"}{p.live_tested ? " · live getestet ✓" : ""} · {TYPE_LABEL[p.type]}
      </div>
      <button onClick={() => setOpen(!open)} data-testid={`catalog-details-${p.id}`} className="mt-4 inline-flex items-center gap-1 text-sm text-blue-400">
        Setup & Details <ChevronDown className={`h-4 w-4 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div className="mt-3 space-y-3 border-t border-white/5 pt-3">
          <ol className="list-decimal space-y-1 pl-5 text-sm text-slate-300">{p.setup_steps.map((s) => <li key={s}>{s}</li>)}</ol>
          <Row k="Standardmodell" v={p.default_model || "wird live ermittelt"} />
          <Row k="Verfügbarkeit" v={p.eligibility_notes} />
          <Row k="Kommerziell" v={p.commercial_notes} />
          <Row k="Datenschutz" v={p.privacy_notes} />
          <Row k="Quelle" v={p.verification_source} />
        </div>
      )}
      <div className="mt-auto flex flex-wrap gap-2 pt-5">
        <Link to={`/app/providers#${p.id}`} data-testid={`catalog-setup-${p.id}`} className="btn-grad inline-flex items-center gap-1.5 px-4 py-2 text-sm"><KeyRound className="h-4 w-4" />Einrichten</Link>
        <a href={p.docs_url} target="_blank" rel="noreferrer" className="btn-ghost inline-flex items-center gap-1.5 px-3 py-2 text-sm text-slate-200">Doku <ExternalLink className="h-3.5 w-3.5" /></a>
        {p.key_url && <a href={p.key_url} target="_blank" rel="noreferrer" className="btn-ghost inline-flex items-center gap-1.5 px-3 py-2 text-sm text-slate-200">Key holen <ExternalLink className="h-3.5 w-3.5" /></a>}
      </div>
    </article>
  );
};

export default function Catalog() {
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");
  const [q, setQ] = useState("");
  const [cat, setCat] = useState("all");
  const [type, setType] = useState("all");
  useEffect(() => { api.get("/catalog").then((r) => setData(r.data)).catch((e) => setErr(errMsg(e))); }, []);
  const list = useMemo(() => (data?.providers || []).filter((p) =>
    (cat === "all" || p.cost_status === cat) && (type === "all" || p.type === type) && (p.name + p.capabilities.join(" ")).toLowerCase().includes(q.toLowerCase())), [data, q, cat, type]);
  const cats = useMemo(() => [...new Set((data?.providers || []).map((p) => p.cost_status))], [data]);

  return (
    <div data-testid="catalog-page">
      <PageHeader title="API Marketplace" subtitle="Provider-Katalog mit Zugangskategorie, Quelle und Prüfdatum. Kostenlose Kontingente haben Limits und können sich jederzeit ändern – nicht belegte Werte stehen als „unknown“." />
      {err && <div className="text-red-300">{err}</div>}
      <div className="mb-6 flex flex-col gap-4 md:flex-row md:items-center">
        <div className="relative md:w-80"><Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-500" />
          <input data-testid="catalog-search-input" className="field pl-9" placeholder="Provider oder Fähigkeit suchen…" value={q} onChange={(e) => setQ(e.target.value)} /></div>
        <div className="flex flex-wrap gap-2">
          {["all", ...cats].map((c) => (
            <button key={c} data-testid={`catalog-filter-${c}`} onClick={() => setCat(c)}
              className={`rounded-lg px-3 py-1.5 text-sm transition-colors ${cat === c ? "bg-blue-500 text-white" : "btn-ghost text-slate-300"}`}>
              {c === "all" ? "Alle" : CATEGORY_META[c]?.label || c}
            </button>
          ))}
        </div>
        <select data-testid="catalog-type-filter" className="field md:w-52" value={type} onChange={(e) => setType(e.target.value)}>
          <option value="all">Alle Typen</option>
          {Object.entries(TYPE_LABEL).map(([k, l]) => <option key={k} value={k}>{l}</option>)}
        </select>
      </div>
      <div className="mb-4 text-xs text-slate-500" data-testid="catalog-count">{list.length} von {data?.providers?.length || 0} Providern · Quelle u. a. awesome-freellm-apis (Community-Liste, nicht offiziell) – Status je Karte beachten</div>
      <div className="grid gap-5 md:grid-cols-2 xl:grid-cols-3">{list.map((p) => <ProviderCard key={p.id} p={p} />)}</div>
    </div>
  );
}
